/**
 * CardList — the sub-`lg` (tablet/phone) rendering of tabular data.
 *
 * The DataGrid reflows into this below `lg` so there is NEVER horizontal body
 * scroll on a tablet (hard rule). Each row becomes a tappable card that honours
 * the 44px touch floor. The caller supplies a per-row renderer so the card
 * layout is domain-appropriate (a device card != a client card).
 */
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";

interface CardListProps<T> {
  rows: T[];
  rowKey: (row: T) => string;
  renderCard: (row: T) => ReactNode;
  onRowActivate?: (row: T) => void;
  empty?: ReactNode;
}

export function CardList<T>({
  rows,
  rowKey,
  renderCard,
  onRowActivate,
  empty,
}: CardListProps<T>) {
  if (rows.length === 0) {
    // Tablet/phone reflow has no surrounding grid card, so the empty state brings its own: the
    // neutral tray glyph in a soft disc over the caller's message, matching the desktop grid's
    // empty row so the two viewports read the same.
    return (
      <div className="md-card md-enter flex flex-col items-center px-6 py-12 text-center">
        <span aria-hidden className="mb-3 grid h-12 w-12 place-items-center rounded-full bg-surface-2 text-ink-subtle">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 13h4l2 3h6l2-3h4" />
            <path d="M5 13V6a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v7" />
          </svg>
        </span>
        <div className="max-w-md text-sm text-ink-muted">{empty ?? "Nothing here yet."}</div>
      </div>
    );
  }
  return (
    // md-stagger: the cards rise in top-to-bottom on first paint so a tablet list assembles
    // itself rather than snapping in. Reduced-motion collapses it to an instant render.
    <ul className="md-stagger flex flex-col gap-2">
      {rows.map((row) => {
        const interactive = !!onRowActivate;
        return (
          <li key={rowKey(row)}>
            {/* Whole card is the touch target when a row action exists. */}
            <div
              role={interactive ? "button" : undefined}
              tabIndex={interactive ? 0 : undefined}
              onClick={interactive ? () => onRowActivate?.(row) : undefined}
              onKeyDown={
                interactive
                  ? (e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onRowActivate?.(row);
                      }
                    }
                  : undefined
              }
              className={cn(
                "md-card p-3",
                interactive && "md-lift min-h-touch cursor-pointer hover:bg-surface-2",
              )}
            >
              {renderCard(row)}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
